import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { Bell } from "lucide-react";

export default function App() {
  const [data, setData] = useState([]);
  const [numWinners, setNumWinners] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [winners, setWinners] = useState([]);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [prizeTitle, setPrizeTitle] = useState("");

  const jingleRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    const reader = new FileReader();
    reader.onprogress = (evt) => {
      if (evt.lengthComputable) {
        setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };

    reader.onloadend = () => {
      setUploadProgress(100);
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    };

    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      setData(XLSX.utils.sheet_to_json(ws));
    };

    reader.readAsBinaryString(file);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Participants");
    XLSX.writeFile(wb, "christmas_raffle.xlsx");
  };

  const startDraw = () => {
    if (!prizeTitle) return alert("Please enter a prize title");
    if (numWinners < 1) return alert("Please select number of winners");

    setLoading(true);
    setWinners([]);
    setShowConfetti(false);

    // 🔔 play jingle
    if (jingleRef.current) {
      jingleRef.current.currentTime = 0;
      jingleRef.current.play();
    }

    setTimeout(() => {
      const filtered = data.filter(
        (d) => !winnersHistory.some((w) => w.name === d.name && w.prize === prizeTitle)
      );

      if (filtered.length < numWinners) {
        setLoading(false);
        return alert("Not enough participants left for this prize!");
      }

      const selected = [...filtered]
        .sort(() => 0.5 - Math.random())
        .slice(0, numWinners)
        .map((w) => ({ ...w, prize: prizeTitle }));

      setWinners(selected);
      setWinnersHistory((prev) => [...prev, ...selected]);
      setLoading(false);
      setShowConfetti(true);

      setTimeout(() => setShowConfetti(false), 5000);
    }, 3000);
  };

  const stopJingle = () => {
    if (jingleRef.current) {
      jingleRef.current.pause();
      jingleRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="min-h-screen p-6 text-white relative overflow-hidden"
      style={{
        backgroundImage:
          "url('/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {showConfetti && <Confetti />}

      {/* Jingle Sound */}
      <audio ref={jingleRef} src="/jingle.mp3" preload="auto" />

      <div className="absolute inset-0 bg-black/50" />

      <div className="relative z-10">
        <h1 className="text-4xl font-extrabold text-center mb-8 drop-shadow-lg">
          🎄 SDO Kidapawan Raffle Draw System 🎄
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <div className="space-y-6">
            {/* File Upload */}
            <div className="bg-white/95 text-gray-800 p-6 rounded-2xl shadow-xl">
              <div className="p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded mb-2">
                ⚠️ Upload an Excel file listing the participants with three columns: <strong> id, name, and office.</strong>
Make sure all column headers are in lowercase.

              </div>

              <label className="font-semibold text-red-700">Upload Excel File</label>
              <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />

              {uploading && (
                <div className="mt-4 bg-gray-200 rounded-full h-4">
                  <motion.div
                    animate={{ width: `${uploadProgress}%` }}
                    className="h-4 bg-green-600 rounded-full"
                  />
                </div>
              )}
            </div>

            {/* Prize and Number of Winners */}
            {data.length > 0 && (
              <div className="bg-white/95 text-gray-800 p-6 rounded-2xl shadow-xl space-y-4">
                <label className="font-semibold text-green-700">Prize Title</label>
                <input
                  type="text"
                  value={prizeTitle}
                  onChange={(e) => setPrizeTitle(e.target.value)}
                  placeholder="Enter prize title"
                  className="w-full border rounded p-2"
                />

                <label className="font-semibold text-green-700">Number of Winners</label>
                <input
                  type="number"
                  min="1"
                  value={numWinners}
                  onChange={(e) => setNumWinners(Number(e.target.value))}
                  className="w-full border rounded p-2"
                />

                <div className="flex gap-2">
                  <button
                    onClick={startDraw}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded-xl flex items-center justify-center gap-2"
                  >
                    <Bell /> 🎁 Start Draw
                  </button>
                  <button
                    onClick={stopJingle}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 rounded-xl"
                  >
                    ⏹ Stop Music
                  </button>
                </div>

                <button
                  onClick={exportToExcel}
                  className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white font-bold py-2 rounded-xl"
                >
                  ⛄ Export Excel
                </button>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <motion.div
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 3 }}
                className="h-3 bg-yellow-400 rounded-full"
              />
            )}

            {/* Winners */}
            {winners.length > 0 && (
              <div className="bg-white/95 text-gray-800 p-6 rounded-2xl shadow-xl">
                <h2 className="text-xl font-bold text-red-700 mb-4">
                  🎆 Winners for {prizeTitle} 🎆
                </h2>
                {winners.map((w, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: i * 0.3 }}
                    className="p-3 mb-2 rounded-xl bg-red-50 border border-red-300"
                  >
                    <strong>{w.name}</strong> ({w.office})
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Winners History */}
          <div className="bg-white/95 text-gray-800 p-6 rounded-2xl shadow-xl">
            <h2 className="font-bold text-green-700 mb-2">🏆 Winners History</h2>
            <ul className="space-y-2 max-h-[400px] overflow-y-auto">
              {winnersHistory.map((w, i) => (
                <li key={i} className="border-b pb-1 text-sm">
                  {w.name} – {w.office} – {w.prize}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <p className="text-center mt-6 text-sm text-white/80">
        Powered by SDO Kidapawan City
      </p>
    </div>
  );
}
